package domain.model

import kotlinx.serialization.Serializable

/**
 * Enhanced pull request model with organization, team, and SSO context
 */
@Serializable
data class PullRequestInsights(
    val id: String,
    val number: Int,
    val title: String,
    val htmlUrl: String,
    val state: String,
    val isDraft: Boolean,
    val createdAt: String,
    val updatedAt: String,
    val author: Author,
    val repository: Repository,
    val labels: List<Label>,
    val reviewDecision: String?,
    val totalCommentsCount: Int,
    val additions: Int,
    val deletions: Int,
    val changedFiles: Int,
    val mergeable: String?,
    val baseRefName: String,
    val headRefName: String,
    val organization: GitHubOrganization?,
    val teams: List<Team>,
    val assignees: List<Author>,
    val reviewRequests: List<ReviewRequest>,
    val reviews: List<Review>
)

@Serializable
data class Author(
    val login: String,
    val name: String?,
    val avatarUrl: String,
    val htmlUrl: String
)

@Serializable
data class Repository(
    val id: String,
    val name: String,
    val nameWithOwner: String,
    val htmlUrl: String,
    val isPrivate: Boolean,
    val primaryLanguage: String?,
    val organization: RepositoryOrganization?
)

@Serializable
data class RepositoryOrganization(
    val login: String,
    val name: String?,
    val avatarUrl: String
)

@Serializable
data class Label(
    val name: String,
    val color: String,
    val description: String?
)

@Serializable
data class Team(
    val id: String,
    val name: String,
    val slug: String,
    val description: String?,
    val privacy: String,
    val organization: TeamOrganization
)

@Serializable
data class TeamOrganization(
    val login: String,
    val name: String?
)

@Serializable
data class ReviewRequest(
    val requestedReviewer: Author?
)

@Serializable
data class Review(
    val id: String,
    val author: Author,
    val state: String,
    val submittedAt: String?
)

/**
 * View filtering modes for PRs
 */
enum class PRViewMode {
    ALL_PRS,           // All pull requests
    ONLY_MINE,         // Only PRs authored by current user
    EVERYTHING_EXCEPT_MINE, // All PRs except those authored by current user
    TEAM_ONLY          // Only PRs from user's teams
}

/**
 * Organization context for PR filtering
 */
@Serializable
data class OrganizationContext(
    val organization: GitHubOrganization,
    val userTeams: List<Team>,
    val isUserMember: Boolean,
    val canViewPrivateRepos: Boolean
)
